import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  SearchInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, PlusCircleIcon, SearchIcon, SyncAltIcon, TagIcon, TagsIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { NotFound } from '@app/NotFound/NotFound';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useAdminProducts } from '../../../hooks/useAdminProducts';
import type { AdminProduct } from '../../../types/models/AdminProduct';
import AdminProductRow from './AdminProductRow';
import ClientPricesModal from './ClientPricesModal';
import CreateProductModal from './CreateProductModal';
import EditProductModal from './EditProductModal';
import ManageCategoriesModal from './ManageCategoriesModal';

const COLUMN_COUNT = 7;

// A flattened display row: either a category group header or a product, interleaved so the table
// renders category groups by walking the backend order (products are ordered by category then label).
type DisplayRow =
  | { kind: 'group'; categoryId: number; categoryName: string; count: number }
  | { kind: 'product'; product: AdminProduct };

/**
 * Products and Prices — an Ancalagon-staff-only Admin page for managing the product catalog and
 * per-client pricing. These products and prices are the building blocks a future feature round will
 * use to assemble customer quotes (see the sibling Quotes page).
 *
 * The catalog grid grouped by category, with an inline default-price editor, client-side search, a
 * per-client overrides modal, and category management (CRUD + per-product category reassignment).
 *
 * Gating is enforced in two independent places (defense in depth):
 *   1. AppLayout hides the whole "Admin" nav group unless `user.is_ancalagon_staff`.
 *   2. This page guards itself: a non-staff user who types `/admin/products-and-prices` directly
 *      gets <NotFound /> — the same response as a non-existent route, so the admin area's
 *      existence is never leaked. (The backend `/admin` routes enforce this authoritatively;
 *      this is the frontend mirror.)
 */
function ProductsAndPrices() {
  const auth = useAuth();
  const user = useUserContext();
  // Defer the GET until the staff check passes (the guard below returns <NotFound /> for non-staff),
  // so a non-staff direct visit never fires the backend-rejected admin call.
  const { products, loading, error, refetch, applyProduct } = useAdminProducts(
    user?.is_ancalagon_staff === true,
  );

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the search so filtering doesn't run on every keystroke (CLAUDE.md: 400ms).
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchValue), 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  // Client-side filter on product label + key (the dataset is small — one GET, ~tens of rows).
  const filteredProducts = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) {
      return products;
    }
    return products.filter(
      (p) => p.label.toLowerCase().includes(q) || p.product_key.toLowerCase().includes(q),
    );
  }, [products, debouncedSearch]);

  // Group by category, then emit groups in canonical category-id order (id = display order) and
  // products in label order. Sorting explicitly — rather than relying on the fetched array order —
  // keeps the grid correct after an in-place category reassignment, where applyProduct replaces the
  // row without re-sorting (so a moved product would otherwise leave its group out of order).
  const displayRows = useMemo<DisplayRow[]>(() => {
    const groups = new Map<number, { name: string; products: AdminProduct[] }>();
    filteredProducts.forEach((p) => {
      const existing = groups.get(p.category.id);
      if (existing) {
        existing.products.push(p);
      } else {
        groups.set(p.category.id, { name: p.category.name, products: [p] });
      }
    });
    const rows: DisplayRow[] = [];
    Array.from(groups.entries())
      .sort(([a], [b]) => a - b)
      .forEach(([categoryId, group]) => {
        rows.push({ kind: 'group', categoryId, categoryName: group.name, count: group.products.length });
        [...group.products]
          .sort((x, y) => x.label.localeCompare(y.label))
          .forEach((product) => rows.push({ kind: 'product', product }));
      });
    return rows;
  }, [filteredProducts]);

  // Per-client overrides modal: which product (if any) is being managed.
  const [overridesProduct, setOverridesProduct] = useState<AdminProduct | null>(null);

  // Keep the grid row's override badge in sync when the modal adds/removes overrides.
  const handleOverrideCountChange = useCallback(
    (productId: number, count: number) => {
      const target = products.find((p) => p.id === productId);
      if (target) {
        applyProduct({ ...target, override_count: count });
      }
    },
    [products, applyProduct],
  );

  // Create-product modal, clone modal (which source product), full-modification modal (which
  // product) + manage-categories modal (open?).
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [cloneProduct, setCloneProduct] = useState<AdminProduct | null>(null);
  const [editProduct, setEditProduct] = useState<AdminProduct | null>(null);
  const [isManageCategoriesOpen, setIsManageCategoriesOpen] = useState(false);

  // Product counts per category id — drives the manage-categories delete guard (a category can't be
  // deleted while products reference it).
  const categoryUsage = useMemo(() => {
    const usage = new Map<number, number>();
    products.forEach((p) => usage.set(p.product_category_id, (usage.get(p.product_category_id) ?? 0) + 1));
    return usage;
  }, [products]);

  // A category rename changes the name embedded in product rows, so re-fetch the catalog to re-group.
  const handleCategoriesChanged = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  // Ancalagon-staff-only. Treat anything other than an explicit `true` as "no access" and
  // render the 404 page so the route reveals nothing about the admin area's existence.
  if (user?.is_ancalagon_staff !== true) {
    return <NotFound />;
  }

  const renderContent = () => {
    // Show the full-page spinner only on the genuine initial load (and on Retry after a failed
    // load), when there's nothing to show yet. During a manual Refresh the populated table stays
    // visible and only the title-bar button spins (matching DomainNameDetail's Refresh convention).
    if (loading && products.length === 0) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load products"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (products.length === 0) {
      return (
        <EmptyState titleText="No products yet" icon={TagsIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>The product catalog is empty.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Create product
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by product name or key..."
                value={searchValue}
                onChange={(_event, value) => setSearchValue(value)}
                onClear={() => setSearchValue('')}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>
        {filteredProducts.length === 0 ? (
          <EmptyState
            titleText="No products match your search"
            icon={SearchIcon}
            variant="lg"
            headingLevel="h2"
          >
            <EmptyStateBody>Try a different search term, or clear the filter to see all products.</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={() => setSearchValue('')}>
                  Clear filter
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <Table aria-label="Product catalog and prices" variant="compact">
            <Thead>
              <Tr>
                <Th>Product</Th>
                <Th>Billing</Th>
                <Th>Default price</Th>
                <Th>Overrides</Th>
                <Th>Active</Th>
                <Th>Visibility</Th>
                <Th screenReaderText="Row actions" />
              </Tr>
            </Thead>
            <Tbody>
              {displayRows.map((row) =>
                row.kind === 'group' ? (
                  <Tr key={`group-${row.categoryId}`} className="ancalagon-product-group-header">
                    <Td colSpan={COLUMN_COUNT}>
                      {row.categoryName}{' '}
                      <Content component="small">
                        ({row.count} {row.count === 1 ? 'product' : 'products'})
                      </Content>
                    </Td>
                  </Tr>
                ) : (
                  <AdminProductRow
                    key={row.product.id}
                    product={row.product}
                    onUpdated={applyProduct}
                    onManageOverrides={setOverridesProduct}
                    onEdit={setEditProduct}
                    onClone={setCloneProduct}
                  />
                ),
              )}
            </Tbody>
          </Table>
        )}
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Admin</BreadcrumbItem>
          <BreadcrumbItem isActive>Products and Prices</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <TagsIcon />
              </Icon>
              Products and Prices
            </Content>
            <Content component="p">Manage the product catalog and per-client pricing</Content>
          </FlexItem>
          <FlexItem>
            <Flex spaceItems={{ default: 'spaceItemsSm' }}>
              {/* Create-style primary action: hidden only during the genuine initial load (when the
                  content area is the full-page spinner); shown for the loaded, empty, and error states. */}
              {!(loading && products.length === 0) && (
                <FlexItem>
                  <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                    Create product
                  </Button>
                </FlexItem>
              )}
              <FlexItem>
                <Button variant="secondary" icon={<TagIcon />} onClick={() => setIsManageCategoriesOpen(true)}>
                  Manage categories
                </Button>
              </FlexItem>
              <FlexItem>
                <Button
                  variant="secondary"
                  icon={<SyncAltIcon />}
                  onClick={() => void refetch()}
                  isLoading={loading}
                  isDisabled={loading}
                >
                  Refresh
                </Button>
              </FlexItem>
            </Flex>
          </FlexItem>
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>
      {isCreateOpen && (
        <CreateProductModal isOpen onClose={() => setIsCreateOpen(false)} onCreated={() => void refetch()} />
      )}
      {cloneProduct && (
        <CreateProductModal
          key={`clone-${cloneProduct.id}`}
          isOpen
          cloneSource={cloneProduct}
          onClose={() => setCloneProduct(null)}
          onCreated={() => void refetch()}
        />
      )}
      {overridesProduct && (
        <ClientPricesModal
          key={overridesProduct.id}
          product={overridesProduct}
          isOpen
          onClose={() => setOverridesProduct(null)}
          onOverrideCountChange={handleOverrideCountChange}
        />
      )}
      {editProduct && (
        <EditProductModal
          key={editProduct.id}
          product={editProduct}
          isOpen
          onClose={() => setEditProduct(null)}
          onUpdated={applyProduct}
        />
      )}
      {isManageCategoriesOpen && (
        <ManageCategoriesModal
          isOpen
          onClose={() => setIsManageCategoriesOpen(false)}
          categoryUsage={categoryUsage}
          onChanged={handleCategoriesChanged}
        />
      )}
    </>
  );
}

export { ProductsAndPrices };
